import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, forkJoin, of } from 'rxjs';
import { catchError, first, mergeMap, take, toArray } from 'rxjs/operators';
import { MetadataSet } from '~app/core/models/metadata-set.model';
import { NotificationService } from '~core/core.module';
import { MetadataDefinitionService } from '~core/data/metadata-definition.service';
import { MetadataSetService } from '~core/data/metadata-set.service';
import { MetadataService } from '~core/data/metadata.service';
import { MetadataDefinition } from '~core/models/metadata-definition.model';
import { Metadata } from '~core/models/metadata.model';
import { ProgressDialogComponent } from '~core/utils/progress-dialog/progress-dialog.component';
import { MetadataIoModule } from './metadata-io.module';
import { ExportAppMetadata } from './models/export-app-metadata.model';
import { ExportMetadataSet } from './models/export-metadata-set.model';
import { ExportMetadata } from './models/export-metadata.model';
import { UploadMetadataComponent } from './upload-metadata/upload-metadata.component';

@Injectable({
  providedIn: MetadataIoModule
})
export class MetadataIoService {
  private _progressDialog: MatDialogRef<any>;
  private _progress$ = new Subject();

  constructor(
    private _dialog: MatDialog,
    private _metadataDefinitionService: MetadataDefinitionService,
    private _metadataService: MetadataService,
    private _metadatasetService: MetadataSetService,
    private readonly _notificationService: NotificationService
  ) {}

  exportMetadata(
    applicationId: number,
    metadatadefinitions: MetadataDefinition[]
  ) {
    const exportData = new ExportAppMetadata(metadatadefinitions, []);

    this._metadatasetService
      .list(applicationId)
      .pipe(
        take(1),
        mergeMap(metadataSets => metadataSets),
        mergeMap(
          metadataSet =>
            this._metadataService
              .list(applicationId, metadataSet.metadataSetId)
              .pipe(take(1)),
          (metadataSet, metadatas) => {
            const exportMetadatas: ExportMetadata[] = [];
            metadatas.forEach((metadata: Metadata) => {
              const exportMetadata = new ExportMetadata(
                metadata.definitionName,
                metadata.value
              );
              exportMetadatas.push(exportMetadata);
            });

            const exportMetadataSet = new ExportMetadataSet(
              metadataSet.name,
              metadataSet.comments,
              exportMetadatas
            );
            return exportMetadataSet;
          }
        ),
        toArray()
      )
      .subscribe(sets => {
        exportData.sets = sets;

        const newBlob = new Blob([JSON.stringify(exportData, null, 2)], {
          type: 'application/json'
        });

        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob
        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          window.navigator.msSaveOrOpenBlob(newBlob, 'metaData.json');
          return;
        }

        // For other browsers:
        // Create a link pointing to the ObjectURL containing the blob.
        const data = window.URL.createObjectURL(newBlob);

        const link = document.createElement('a');
        link.href = data;
        link.download = 'metaData.json';
        // this is necessary as link.click() does not work on the latest firefox
        link.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window
          })
        );

        setTimeout(function() {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(data);
          link.remove();
        }, 100);
      });
  }

  checkBaseData(appMetadata: ExportAppMetadata): boolean {
    let result = true;
    const valuesSoFar = Object.create(null);
    appMetadata.base.forEach(definition => {
      // Check for duplicate keyname
      if (definition.keyName in valuesSoFar) {
        result = false;
        return false;
      }
      valuesSoFar[definition.keyName] = true;

      // Check that keyType exists
      if (!definition.keyType) {
        result = false;
        return false;
      }
    });

    return result;
  }

  checkSetData(appMetadata: ExportAppMetadata): boolean {
    let result = true;
    const valuesSoFar = Object.create(null);
    appMetadata.sets.forEach(set => {
      // Check for duplicate name
      if (set.name in valuesSoFar) {
        result = false;
        return false;
      }
      valuesSoFar[set.name] = true;

      // Check that definitionName is in base.keyName exists
      set.metadata.forEach(metadata => {
        if (
          appMetadata.base.findIndex(
            def => metadata.definitionName === def.keyName
          ) === -1
        ) {
          result = false;
          return false;
        }
      });
    });

    return result;
  }

  importMetadata(applicationId: number): Promise<any> {
    const dialogRef = this._dialog.open(UploadMetadataComponent, {});

    const promise$ = new Promise<void>((resolve, reject) => {
      dialogRef
        .afterClosed()
        .pipe(
          mergeMap((appMetadata: ExportAppMetadata) => {
            this._progressDialog = this._dialog.open(ProgressDialogComponent, {
              maxWidth: '650px',
              width: '90%',
              disableClose: true,
              data: {
                message: 'Importing Metadata',
                progress$: this._progress$
              }
            });

            if (!appMetadata) {
              // No data provided
              return of(false);
            }
            // check base definitions
            if (!this.checkBaseData(appMetadata)) {
              // failed
              throw new Error('import failed: bad base data');
            }

            // check sets
            if (!this.checkSetData(appMetadata)) {
              // failed
              throw new Error('import failed: bad set data');
            }

            // Progress tested incoming data
            this._progress$.next(20);

            return forkJoin(
              of(appMetadata),
              this._metadataDefinitionService.listall(applicationId),
              this._metadatasetService.list(applicationId)
            );
          }),
          mergeMap(lists => {
            // console.log('Got lists');
            let defDeletes$: Observable<Object>[] = [];
            if (lists[1].length > 0) {
              defDeletes$ = lists[1].map(definition =>
                this._metadataDefinitionService
                  .delete(applicationId, definition.metadataDefinitionId)
                  .pipe(first())
              );
            }

            let setDeletes$: Observable<Object>[] = [];
            if (lists[2].length > 0) {
              setDeletes$ = lists[2].map(set =>
                this._metadatasetService
                  .delete(applicationId, set.metadataSetId)
                  .pipe(first())
              );
            }

            if (lists[1].length > 0 || lists[2].length > 0) {
              return forkJoin(of(lists[0]), ...defDeletes$, ...setDeletes$);
            } else {
              return forkJoin(of(lists[0]), 'No data to delete');
            }
          }),
          mergeMap(returns => {
            // Progress deleted old data
            this._progress$.next(40);

            // Add Definitions
            const appMetadata: ExportAppMetadata = returns[0];
            // console.log('Add definitions');
            const defEdits$ = appMetadata.base.map(definition =>
              this._metadataDefinitionService
                .edit(applicationId, definition)
                .pipe(first())
            );
            return forkJoin(of(appMetadata), ...defEdits$);
          }),
          mergeMap(definitions => {
            // Progress added definitions
            this._progress$.next(60);

            const appMetadata: ExportAppMetadata = definitions[0];

            const jobs$ = appMetadata.sets.map(set => {
              // console.log('Add set');
              return this._metadatasetService
                .edit(
                  applicationId,
                  new MetadataSet(
                    0,
                    0,
                    set.name,
                    set.title,
                    set.comments,
                    new Date(),
                    1
                  )
                )
                .pipe(
                  first(),
                  mergeMap(newSet => {
                    // console.log('Get set data');
                    return this._metadataService
                      .list(applicationId, newSet.metadataSetId)
                      .pipe(
                        first(),
                        mergeMap(metadatas => {
                          const dataEdits$ = set.metadata.map(data => {
                            const metadata = metadatas.find(
                              x => x.definitionName === data.definitionName
                            );
                            metadata.value = data.value;
                            return this._metadataService
                              .edit(applicationId, metadata)
                              .pipe(first());
                          });
                          return forkJoin(...dataEdits$);
                        })
                      );
                  })
                );
            });

            return forkJoin(...jobs$);
          }),
          catchError(error => {
            throw error;
          }),
          take(1)
        )
        .subscribe({
          next: res => {
            if (!res)
              this._notificationService.warn(
                'Import halted, no data provided.'
              );

            // Progress added sets
            this._progress$.next(100);
          },
          error: error => {
            this._notificationService.error(error);
            this._progressDialog.close();
            reject(error);
          },
          complete: () => {
            this._progressDialog.close();
            resolve();
          }
        });
    });

    return promise$;
  }
}
